import { EDeviceTypeName } from '/@/types'
import { OSDVisible } from '/@/types/device'
import type { RootStateType } from '/@/store'

export interface OsdMapTarget {
  sn: string
  type: EDeviceTypeName
  name: string
  longitude: number
  latitude: number
}

function isValidCoord (longitude?: number, latitude?: number): boolean {
  if (longitude == null || latitude == null) return false
  if (Number.isNaN(longitude) || Number.isNaN(latitude)) return false
  if (longitude === 0 && latitude === 0) return false
  return true
}

/** Resolve map marker targets for the currently selected OSD device (drone / dock). */
export function getOsdMapTargets (
  osd: OSDVisible,
  deviceState: RootStateType['deviceState'],
): OsdMapTarget[] {
  if (!osd.visible) return []

  const targets: OsdMapTarget[] = []

  if (osd.is_dock && osd.gateway_sn) {
    const dock = deviceState.dockInfo[osd.gateway_sn]
    const longitude = dock?.basic_osd?.longitude
    const latitude = dock?.basic_osd?.latitude
    if (isValidCoord(longitude, latitude)) {
      targets.push({
        sn: osd.gateway_sn,
        type: EDeviceTypeName.Dock,
        name: osd.gateway_callsign || osd.gateway_sn,
        longitude: longitude!,
        latitude: latitude!,
      })
    }
  }

  if (osd.sn) {
    const aircraft = deviceState.deviceInfo[osd.sn]
    if (isValidCoord(aircraft?.longitude, aircraft?.latitude)) {
      targets.push({
        sn: osd.sn,
        type: EDeviceTypeName.Aircraft,
        name: osd.callsign || osd.sn,
        longitude: aircraft!.longitude,
        latitude: aircraft!.latitude,
      })
    }
  }

  return targets
}

/** Primary target to center the map. */
export function getOsdMapFocusTarget (
  targets: OsdMapTarget[],
  options?: { preferDock?: boolean },
): OsdMapTarget | null {
  if (targets.length === 0) return null
  if (options?.preferDock) {
    const dock = targets.find(t => t.type === EDeviceTypeName.Dock)
    if (dock) return dock
  }
  const aircraft = targets.find(t => t.type === EDeviceTypeName.Aircraft)
  return aircraft ?? targets[targets.length - 1]
}
